import { useEffect, useRef } from 'react';
import NotificationsApi from 'services/api/common/NotificationsApi';
import { isEmpty } from 'lodash';
import { getAccountId, getUserId } from 'store/auth/selectors';
import { shallowEqual, useSelector } from 'react-redux';
import logging from 'domain/logging';

// Custom hook to send tracking notifications every 20 minutes
function useTrackingNotificationsSync() {
  const userId = useSelector(getUserId, shallowEqual);
  const accountId = useSelector(getAccountId, shallowEqual);

  const INTERVAL_MS = 20 * 60 * 1000; // 20 minutes
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const localStorageKey = `tracking_notifications.${accountId}.${userId}`;
  const localStorageTimeKey = `tracking_notifications_last_sync.${accountId}.${userId}`;

  useEffect(() => {
    // Get last sync time from localStorage
    const lastSyncRaw = localStorage.getItem(localStorageTimeKey);
    // If no last sync, initialize it
    if (!lastSyncRaw) {
      localStorage.setItem(localStorageTimeKey, Date.now().toString());
    }
    // Function to send notifications and update last sync time
    const syncFunction = async () => {
      const raw = localStorage.getItem(localStorageKey);
      if (!raw || isEmpty(raw)) return;
      let notifications = null;
      try {
        notifications = JSON.parse(raw);
      } catch (e) {
        logging.error('Failed to parse tracking_notifications:', e);
        return;
      }
      // Only send if there are notifications
      if (Array.isArray(notifications) && !isEmpty(notifications)) {
        try {
          await NotificationsApi.sendTrackingNotifications({
            data: notifications,
          });
          localStorage.removeItem(localStorageKey);
        } catch (error) {
          logging.error('Failed to send tracking notifications:', error);
        }
      }
      // Update last sync time after sending
      localStorage.setItem(localStorageTimeKey, Date.now().toString());
    };
    // Calculate delay for first sync after reload
    const lastSync = lastSyncRaw ? parseInt(lastSyncRaw, 10) : null;
    const now = Date.now();
    let delayMs = INTERVAL_MS;
    if (lastSync && now - lastSync < INTERVAL_MS) {
      // Wait remaining time if last sync was recent
      delayMs = INTERVAL_MS - (now - lastSync);
    } else {
      // Sync immediately if last sync was long ago or missing
      delayMs = 0;
    }

    // Cleanup any existing timers before setting new ones
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    // Schedule first sync, then regular interval
    timeoutRef.current = setTimeout(() => {
      syncFunction();
      intervalRef.current = setInterval(syncFunction, INTERVAL_MS);
    }, delayMs);

    // Cleanup timers on unmount
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);
}

export default useTrackingNotificationsSync;
